import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { Box, Flex, Link } from "theme-ui";
import moment from "moment-timezone";

import { themevals } from "theme/themevals";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import TableData from "components/ui/TableData";
import BasicButton from "components/forms/BasicButton";
import BasicModal from "components/ui/BasicModal";
import { useToast } from "components/toast";
import { BackArrowIcon } from "components/svg";
import { Pill } from "containers/Leads/List";

import OpportunityDrawer, { DuePill, STAGE_LABEL } from "./Drawer";
import { PROPOSAL_PILL } from "./GeneratedProposals";
import ReviewSend from "./ReviewSend";
import CombineSend from "./CombineSend";
import {
  detectMo,
  dismissMo,
  estimateFindContacts,
  findContacts,
  getMoCounts,
  getMoFacets,
  getMoTable,
  refreshMoProposalCopy,
  MO_DISMISS_LABELS,
  type MoBucket,
  type MoCounts,
  type MoDismissReason,
  type MoFacets,
  type MoRow,
} from "api/MaintenanceOpportunities";

/**
 * Leads > Maintenance Opportunities: a WORK QUEUE of completed sites whose
 * mulch is coming due (MAINTENANCE_OPPORTUNITY_PHASES.md phase 1). Sorted
 * by due date so the top row is the most overdue. Every row states its next
 * step; clicking a row opens the drawer where that step is done. Bulk
 * actions work on the ticked rows of the current page.
 */

const columnHelper = createColumnHelper();
const createColumn = (id: string, title: string, accessor: (row: any) => any, cell?: (info: any) => any, options = {}) =>
  columnHelper.accessor(accessor, { id, cell: cell || ((info) => info.getValue()), header: () => <span>{title}</span>, ...options });

const BUCKETS: Array<{ key: MoBucket; label: string; count: (c: MoCounts) => number }> = [
  { key: "open", label: "Open", count: (c) => c.open },
  { key: "overdue", label: "Overdue", count: (c) => c.overdue },
  { key: "due_soon", label: "Due soon", count: (c) => c.dueSoon },
  { key: "later", label: "Later", count: (c) => c.later },
  { key: "leads", label: "Leads created", count: (c) => c.leads },
  { key: "proposal_sent", label: "Proposal sent", count: (c) => c.proposalSent },
  { key: "won", label: "Won", count: (c) => c.won },
  { key: "client", label: "Client upsells", count: (c) => c.clientUpsells },
  { key: "dismissed", label: "Dismissed", count: (c) => c.dismissed },
];

const NEXT_LABEL: Record<string, string> = {
  identify: "Find owner",
  create_lead: "Create lead",
  create_proposal: "Create proposal",
  send_proposal: "Copy link + password",
  finish_proposal: "Finish proposal",
  follow_up: "Follow up",
  client: "Offer to client",
  none: "",
};

const ctl: React.CSSProperties = { border: "1px solid #E4E7EB", borderRadius: 6, padding: "6px 8px", fontFamily: "Poppins", fontSize: 12, background: "#fff", maxWidth: 260 };
const sub: React.CSSProperties = { fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey };

const MaintenanceOpportunities: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const bucket = (params.get("bucket") as MoBucket) || "open";
  const openId = params.get("open");
  // Filters + sort above the table (2026-10-16, Matthew: "only Kompan's",
  // "sort by yards"). Kept in the URL so a filtered view can be shared.
  const installer = params.get("installer") || "";
  const siteState = params.get("state") || "";
  const minYards = params.get("minYards") || "";
  const sort = params.get("sort") || "";
  const known = params.get("known") || "";
  const [facets, setFacets] = useState<MoFacets>({ installers: [], states: [] });
  useEffect(() => { getMoFacets().then(setFacets); }, []);
  const setParam = (k: string, v: string) => {
    setSelected({});
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    next.delete("open");
    setParams(next);
  };
  const [counts, setCounts] = useState<MoCounts | null>(null);
  const [tableKey, setTableKey] = useState(0);
  const [pageRows, setPageRows] = useState<MoRow[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [estimate, setEstimate] = useState<string | null>(null);
  const [dismissing, setDismissing] = useState<{ reason: MoDismissReason; note: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reviewIds, setReviewIds] = useState<string[] | null>(null);
  const [combineIds, setCombineIds] = useState<string[] | null>(null);

  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const selectedRows = useMemo(() => pageRows.filter((r) => selected[r.id]), [pageRows, selected]);

  const refreshCounts = () =>
    getMoCounts().then((res: any) => {
      if (res?.error) setError(res.error);
      else { setCounts(res); setError(null); }
    });
  useEffect(() => { refreshCounts(); }, []);

  // The cost on the bulk Find contacts button, recomputed when the selection changes.
  useEffect(() => {
    setEstimate(null);
    if (!selectedIds.length) return;
    let cancelled = false;
    estimateFindContacts(selectedIds).then((e: any) => {
      if (cancelled || e?.error) return;
      if (!e.configured) return setEstimate("lookup not configured");
      setEstimate(e.billable ? `~$${e.estimatedUsd.toFixed(2)}` : "cached, free");
    });
    return () => { cancelled = true; };
  }, [selectedIds.join(",")]); // eslint-disable-line

  const reload = () => {
    setTableKey((k) => k + 1);
    refreshCounts();
  };
  const setBucket = (b: MoBucket) => {
    setSelected({});
    const next = new URLSearchParams(params);
    next.set("bucket", b);
    next.delete("open");
    setParams(next);
  };
  const openRow = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set("open", id); else next.delete("open");
    setParams(next);
  };

  const refresh = async () => {
    setBusy("detect");
    const res: any = await detectMo();
    setBusy(null);
    if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
    toast.add(`Checked ${res.scanned ?? 0} completed sites: ${res.created ?? 0} new, ${res.updated ?? 0} re-dated.`, "var(--theme-ui-colors-success)");
    reload();
  };

  // Proposals issued before 2026-11-03 still offer a depth check in their
  // saved opening line; the share page renders it until it is rewritten.
  const [staleCopy, setStaleCopy] = useState(0);
  useEffect(() => {
    refreshMoProposalCopy(true).then((r: any) => setStaleCopy(r?.error ? 0 : r?.matched ?? 0));
  }, []);
  const fixCopy = async () => {
    setBusy("copy");
    const r: any = await refreshMoProposalCopy(false);
    setBusy(null);
    if (r?.error) return setError(r.error);
    toast.add(`Updated the wording on ${r.changed} proposal${r.changed === 1 ? "" : "s"}.`, "var(--theme-ui-colors-success)");
    const again: any = await refreshMoProposalCopy(true);
    setStaleCopy(again?.error ? 0 : again?.matched ?? 0);
  };

  const bulkFind = async () => {
    setBusy("find");
    const res: any = await findContacts(selectedIds);
    setBusy(null);
    if (res?.pending) {
      // Outlived the request; the server keeps going and saves each row.
      toast.add(`Searching ${selectedIds.length} site${selectedIds.length === 1 ? "" : "s"} - results fill in as each finishes. Refreshing in a minute.`, "var(--theme-ui-colors-warning)");
      setSelected({});
      setTimeout(() => reload(), 60000);
      setTimeout(() => reload(), 150000);
      return;
    }
    if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
    toast.add(
      `Searched ${res.attempted}: ${res.identified} owner${res.identified === 1 ? "" : "s"} found, ${res.emailsFound} address${res.emailsFound === 1 ? "" : "es"}` +
        `${res.clientHits ? `, ${res.clientHits} already client${res.clientHits === 1 ? "" : "s"}` : ""}${res.capped ? " (daily cap reached)" : ""}.`,
      "var(--theme-ui-colors-success)",
    );
    setSelected({});
    reload();
  };

  const bulkDismiss = async () => {
    if (!dismissing) return;
    setBusy("dismiss");
    const res: any = await dismissMo(selectedIds, dismissing.reason, dismissing.note);
    setBusy(null);
    if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
    toast.add(`${res.dismissed} dismissed${res.skipped ? `, ${res.skipped} skipped (already leads)` : ""}.`, "var(--theme-ui-colors-success)");
    setDismissing(null);
    setSelected({});
    reload();
  };

  const generateCount = selectedRows.filter((r) => (r.endCustomer?.name || r.client) && r.priceable !== false && !r.sends?.sent && !["dismissed", "won", "lost"].includes(r.stage)).length;

  const allOnPage = pageRows.length > 0 && pageRows.every((r) => selected[r.id]);

  return (
    <>
      <Header />
      <Page
        title="Opportunities"
        subtitle="Sites coming due for work. Find who owns each one, then send them a plan: one email and one link per person."
        backAction={<Link variant="back" href="/leads"><BackArrowIcon />Back to Leads</Link>}
        titleAction={
          <BasicButton scheme="secondaryOutline" type="button" keepText disabled={busy === "detect"} onClick={refresh}>
            {busy === "detect" ? "Checking…" : "Refresh"}
          </BasicButton>
        }
      >
        {error && (
          <Box sx={{ marginBottom: "16px", padding: "10px 14px", background: "#FBEAEA", color: "#A60000", borderRadius: "8px", fontFamily: "Poppins", fontSize: "13px" }}>{error}</Box>
        )}
        {staleCopy > 0 && (
          <Flex sx={{ gap: "10px", alignItems: "center", flexWrap: "wrap", marginBottom: "12px", padding: "10px 14px", background: "#FFF4E0", color: "#7A4B00", borderRadius: "8px", fontFamily: "Poppins", fontSize: "13px" }}>
            <span style={{ flex: 1 }}>{`${staleCopy} proposal${staleCopy === 1 ? "" : "s"} already sent still offer${staleCopy === 1 ? "s" : ""} a free depth check. Update the wording - same links, prices unchanged.`}</span>
            <BasicButton type="button" keepText disabled={busy === "copy"} onClick={fixCopy}>{busy === "copy" ? "Updating…" : `Update ${staleCopy}`}</BasicButton>
          </Flex>
        )}
        <div style={{ ...sub, marginBottom: 10 }}>
          {counts?.lastDetectAt ? `Last checked ${moment(counts.lastDetectAt).fromNow()}.` : "Not checked yet — press Refresh to find completed sites."}
        </div>

        {/* Buckets */}
        <Flex sx={{ gap: "8px", flexWrap: "wrap", marginBottom: "14px" }}>
          {BUCKETS.map((b) => {
            const on = b.key === bucket;
            return (
              <button
                key={b.key}
                type="button"
                onClick={() => setBucket(b.key)}
                style={{
                  border: `1px solid ${on ? "#000" : "#E4E7EB"}`,
                  background: on ? "#000" : "#fff",
                  color: on ? "#fff" : themevals.colors.dkgrey,
                  borderRadius: 999,
                  padding: "4px 12px",
                  fontFamily: "Poppins",
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                {b.label} <b>{counts ? b.count(counts) : "—"}</b>
              </button>
            );
          })}
        </Flex>

        {/* Filters + sort */}
        <Flex sx={{ gap: "10px", flexWrap: "wrap", alignItems: "center", marginBottom: "14px", fontFamily: "Poppins", fontSize: 12 }}>
          <select style={ctl} value={installer} onChange={(e) => setParam("installer", e.target.value)} aria-label="Installer">
            <option value="">All installers</option>
            {facets.installers.map((i) => (
              <option key={i.id} value={i.id}>{i.name} ({i.open} open)</option>
            ))}
          </select>
          <select style={ctl} value={siteState} onChange={(e) => setParam("state", e.target.value)} aria-label="State">
            <option value="">All states</option>
            {facets.states.map((x) => (
              <option key={x.state} value={x.state}>{x.state} ({x.open} open)</option>
            ))}
          </select>
          <select style={ctl} value={minYards} onChange={(e) => setParam("minYards", e.target.value)} aria-label="Minimum yards">
            <option value="">Any size</option>
            {[25, 50, 100, 200, 400].map((y) => <option key={y} value={String(y)}>{y}+ yd</option>)}
          </select>
          <select style={ctl} value={sort} onChange={(e) => setParam("sort", e.target.value)} aria-label="Sort">
            <option value="">Sort: due soonest</option>
            <option value="yards:desc">Sort: most yards</option>
            <option value="yards:asc">Sort: fewest yards</option>
            <option value="installedAt:desc">Sort: installed newest</option>
            <option value="installedAt:asc">Sort: installed oldest</option>
            <option value="installer:asc">Sort: installer A-Z</option>
            <option value="site:asc">Sort: site A-Z</option>
          </select>
          <select style={ctl} value={known} onChange={(e) => setParam("known", e.target.value)} aria-label="Owner">
            <option value="">Any owner status</option>
            <option value="owner">Owner named</option>
            <option value="email">Owner + email</option>
            <option value="unknown">Owner unknown</option>
          </select>
          {(installer || siteState || minYards || sort || known) && (
            <button type="button" onClick={() => { const next = new URLSearchParams(params); ["installer", "state", "minYards", "sort", "known", "open"].forEach((k) => next.delete(k)); setParams(next); }}
              style={{ background: "none", border: "none", color: "#185FA5", cursor: "pointer", fontFamily: "Poppins", fontSize: 12 }}>
              Clear filters
            </button>
          )}
        </Flex>

        {/* Bulk bar */}
        {selectedIds.length > 0 && (
          <Flex sx={{ gap: "8px", alignItems: "center", flexWrap: "wrap", padding: "8px 12px", background: "#E6F1FB", borderRadius: "8px", marginBottom: "12px", fontFamily: "Poppins", fontSize: 13, color: "#185FA5" }}>
            <span>{selectedIds.length} selected</span>
            <BasicButton type="button" keepText disabled={busy === "find"} onClick={bulkFind}>
              {busy === "find" ? "Searching…" : `Find owners${estimate ? ` · ${estimate}` : ""}`}
            </BasicButton>
            <BasicButton type="button" keepText disabled={!!busy} onClick={() => setReviewIds([...selectedIds])}>
              {`Review & send${selectedRows.length === selectedIds.length ? ` (${generateCount})` : ""}`}
            </BasicButton>
            {selectedIds.length > 1 && (
              <BasicButton type="button" keepText disabled={!!busy} onClick={() => setCombineIds([...selectedIds])}>
                {`Combine into one proposal (${selectedIds.length})`}
              </BasicButton>
            )}
            <BasicButton outline scheme="danger" type="button" keepText onClick={() => setDismissing({ reason: "builder_still_owns", note: "" })}>Dismiss…</BasicButton>
            <BasicButton outline scheme="secondary" type="button" keepText onClick={() => setSelected({})}>Clear</BasicButton>
          </Flex>
        )}

        <TableData
          key={`${bucket}-${installer}-${siteState}-${minYards}-${sort}-${known}-${tableKey}`}
          api={getMoTable}
          stateKey="maintenance-opportunities"
          defaultSortBy="dueAt"
          defaultSortDir="asc"
          defaultPageSize={25}
          defaultFilters={{
            bucket,
            ...(installer ? { installer } : {}),
            ...(siteState ? { state: siteState } : {}),
            ...(minYards ? { minYards } : {}),
            ...(sort ? { sort } : {}),
            ...(known ? { known } : {}),
          }}
          returnData={(res: any) => setPageRows(Array.isArray(res?.items) ? res.items : [])}
          nothingFoundText={bucket === "open" ? "Nothing due. Press Refresh to check completed sites." : "Nothing in this bucket."}
          title={BUCKETS.find((b) => b.key === bucket)?.label ?? "Opportunities"}
          rowStyle={(r: MoRow) => (r.id === openId ? { background: "#F7F9FC" } : undefined)}
          columns={[
            createColumn(
              "select",
              "",
              (r: MoRow) => r?.id,
              (info: any) => {
                const r: MoRow = info.row.original;
                return <input type="checkbox" checked={!!selected[r.id]} onChange={(e) => setSelected((s) => ({ ...s, [r.id]: e.target.checked }))} />;
              },
              {
                enableSorting: false,
                header: () => (
                  <input
                    type="checkbox"
                    checked={allOnPage}
                    onChange={(e) => setSelected((s) => { const n = { ...s }; for (const r of pageRows) n[r.id] = e.target.checked; return n; })}
                  />
                ),
              },
            ),
            createColumn(
              "site",
              "Site",
              (r: MoRow) => r?.site?.projectName ?? r?.site?.name,
              (info: any) => {
                const r: MoRow = info.row.original;
                return (
                  <Box sx={{ cursor: "pointer" }} onClick={() => openRow(r.id)}>
                    <div style={{ fontFamily: "Poppins", fontSize: 13, fontWeight: 600 }}>{r.site.projectName || r.site.name}</div>
                    <div style={sub}>{[r.site.city, r.site.state].filter(Boolean).join(", ")}{r.installer ? ` · via ${r.installer.name}` : ""}</div>
                  </Box>
                );
              },
            ),
            createColumn(
              "installedAt",
              "Installed",
              (r: MoRow) => r?.installedAt,
              (info: any) => {
                const r: MoRow = info.row.original;
                return (
                  <div>
                    <div style={{ fontFamily: "Poppins", fontSize: 13 }}>{r.installedAt ? moment.utc(r.installedAt).format("MMM D, YYYY") : "—"}</div>
                    <div style={sub}>{r.yards != null ? `${r.yards} yd` : ""}{r.product ? ` · ${r.product}` : ""}</div>
                  </div>
                );
              },
            ),
            createColumn(
              "owner",
              "Owner / contacts",
              (r: MoRow) => r?.endCustomer?.name,
              (info: any) => {
                const r: MoRow = info.row.original;
                const st = STAGE_LABEL[r.stage] ?? STAGE_LABEL.candidate;
                const people = (r.trail ?? []).filter((c) => (c.kind === "person" || c.kind === "email") && !c.rejectedAt);
                const ticked = people.filter((c) => c.selected).length;
                return (
                  <Box sx={{ cursor: "pointer" }} onClick={() => openRow(r.id)}>
                    {r.endCustomer?.name ? (
                      <div style={{ fontFamily: "Poppins", fontSize: 13, fontWeight: 500 }}>{r.lead?.name ?? r.client?.name ?? r.endCustomer.name}</div>
                    ) : (
                      <Pill bg="#F1EFE8" fg="#5f5e5a">Owner unknown</Pill>
                    )}
                    <div style={sub}>
                      {r.stage !== "candidate" && <span style={{ marginRight: 6 }}><Pill bg={st.bg} fg={st.fg}>{st.label}</Pill></span>}
                      {people.length ? `${people.length} contact${people.length === 1 ? "" : "s"}${ticked ? `, ${ticked} ticked` : ""}` : r.rfqContact?.name ? `RFQ contact: ${r.rfqContact.name}` : ""}
                    </div>
                  </Box>
                );
              },
              { enableSorting: false },
            ),
            createColumn(
              "proposal",
              "Proposal",
              (r: MoRow) => r?.proposal?.status ?? "none",
              (info: any) => {
                const row = info.row.original as MoRow;
                const p = row.proposal;
                if (row.sends?.sent) {
                  return (
                    <div>
                      <Pill bg={row.sends.won ? "#0F6E56" : "#FFF8E1"} fg={row.sends.won ? "#fff" : "#7A5A00"}>{row.sends.won ? "Accepted" : `Sent to ${row.sends.sent}`}</Pill>
                      <div style={sub}>{row.sends.opened} opened</div>
                    </div>
                  );
                }
                if (!p || p.status === "ready") return <span style={sub}>Not sent</span>;
                const pill = PROPOSAL_PILL[p.status];
                return (
                  <Box sx={{ cursor: "pointer" }} onClick={() => navigate(p.status === "draft" ? `/proposals/${p.id}/edit` : `/proposals/${p.id}`)}>
                    <Pill bg={pill.bg} fg={pill.fg}>{pill.label}</Pill>
                    <div style={sub}>
                      {p.status === "viewed" ? `${p.viewCount} view${p.viewCount === 1 ? "" : "s"}` : p.sentAt ? moment(p.sentAt).fromNow() : ""}
                    </div>
                  </Box>
                );
              },
              { enableSorting: false },
            ),
            createColumn(
              "dueAt",
              "Due",
              (r: MoRow) => r?.dueAt,
              (info: any) => {
                const r: MoRow = info.row.original;
                return <DuePill dueInDays={r.dueInDays} dueAt={r.dueAt} />;
              },
            ),
            createColumn(
              "next",
              "Next step",
              (r: MoRow) => r?.nextStep,
              (info: any) => {
                const r: MoRow = info.row.original;
                // One button per row (2026-10-19): Find owner -> Review & send -> it lives on the leads.
                if (r.sends?.sent) {
                  return r.lead ? <BasicButton outline scheme="secondary" type="button" keepText onClick={() => navigate(`/leads/${r.lead?.id}`)}>Open lead</BasicButton> : null;
                }
                if (r.nextStep === "finish_proposal" && r.proposal) {
                  const pid = r.proposal.id;
                  return <BasicButton outline scheme="secondary" type="button" keepText onClick={() => navigate(`/proposals/${pid}/edit`)}>Finish proposal</BasicButton>;
                }
                if (r.priceable === false) {
                  return <span style={{ fontFamily: "Poppins", fontSize: 11, padding: "3px 8px", borderRadius: 999, background: "#F1EFE8", color: "#5F5E5A" }}>No depot covers this site</span>;
                }
                const hasEmail = (r.trail ?? []).some((c) => c.kind !== "organization" && !c.rejectedAt && c.email) || !!r.endCustomer?.email;
                if ((r.endCustomer?.name || r.client) && hasEmail && !["dismissed", "won", "lost"].includes(r.stage)) {
                  return <BasicButton type="button" keepText onClick={() => setReviewIds([r.id])}>Review &amp; send</BasicButton>;
                }
                const label = r.endCustomer?.name || r.client ? "Add an email" : NEXT_LABEL[r.nextStep] ?? "";
                if (!label) return null;
                return (
                  <BasicButton outline={r.nextStep !== "identify" && r.nextStep !== "create_lead"} scheme={r.nextStep === "identify" || r.nextStep === "create_lead" ? undefined : "secondary"} type="button" keepText onClick={() => openRow(r.id)}>
                    {label}
                    {r.nextStep === "identify" && !r.identifyCached ? " · $0.03" : ""}
                  </BasicButton>
                );
              },
              { enableSorting: false },
            ),
          ]}
        />

        {dismissing && (
          <BasicModal
            title={`Dismiss ${selectedIds.length} opportunit${selectedIds.length === 1 ? "y" : "ies"}`}
            cancel_click={() => setDismissing(null)}
            cancel_text="Cancel"
            confirm_text={busy === "dismiss" ? "Dismissing…" : "Dismiss"}
            submitting={busy === "dismiss"}
            confirm_click={bulkDismiss}
          >
            {(Object.keys(MO_DISMISS_LABELS) as MoDismissReason[]).map((k) => (
              <label key={k} style={{ display: "block", padding: "4px 0", fontFamily: "Poppins", fontSize: 13 }}>
                <input type="radio" checked={dismissing.reason === k} onChange={() => setDismissing({ ...dismissing, reason: k })} /> {MO_DISMISS_LABELS[k]}
              </label>
            ))}
            <input
              style={{ width: "100%", boxSizing: "border-box", border: "1px solid #EBEBEB", borderRadius: 6, padding: "7px 10px", fontFamily: "Poppins", fontSize: 13, marginTop: 8 }}
              placeholder="Note (optional)"
              value={dismissing.note}
              onChange={(e) => setDismissing({ ...dismissing, note: e.target.value })}
            />
            <div style={{ ...sub, marginTop: 8 }}>Rows that already have a lead are skipped. A dismissed site comes back on its own if we mulch it again.</div>
          </BasicModal>
        )}

        {reviewIds && (
          <ReviewSend
            ids={reviewIds}
            onClose={() => { setReviewIds(null); setSelected({}); reload(); refreshCounts(); }}
          />
        )}

        {combineIds && (
          <CombineSend
            ids={combineIds}
            onClose={(sent) => { setCombineIds(null); if (sent) { setSelected({}); reload(); refreshCounts(); } }}
          />
        )}

        {openId && <OpportunityDrawer id={openId} onClose={() => openRow(null)} onChanged={reload} onReviewSend={(oid) => { openRow(null); setReviewIds([oid]); }} />}
      </Page>
    </>
  );
};

export default MaintenanceOpportunities;
